import { useCallback, useEffect, useState } from "react";
import { runGvc, getGvcStatus, getGvcResults } from "../api/client";
import JobLog from "../components/JobLog";

const CHANNELS = ["ml", "sp_ml", "shopee", "amazon", "nvs"];
// "ml" = Mercado Livre inteiro (anúncio Clássico + Premium, um canal só)
const CHANNEL_LABEL = { ml: "Mercado Livre (Clássico + Premium)", sp_ml: "SP-ML (2ª conta ML — SKUs EGSP)" };
const STATUS_COLORS = {
  OK:     "bg-green-100 text-green-800",
  ERRO:   "bg-red-100 text-red-800",
  ALERTA: "bg-yellow-100 text-yellow-800",
};

export default function GVC() {
  const [mode, setMode]       = useState("all");
  const [sku, setSku]         = useState("");
  const [channels, setChannels] = useState([]);
  const [limit, setLimit]     = useState("");

  const [jobId, setJobId]     = useState(null);
  const [running, setRunning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]     = useState("");

  const [results, setResults]         = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);

  const fetchResults = useCallback(async () => {
    setLoadingResults(true);
    try {
      const { data } = await getGvcResults(500);
      setResults(data);
    } catch { /* silencioso */ }
    finally { setLoadingResults(false); }
  }, []);

  // Ao abrir: carrega resultados e verifica se há job rodando
  useEffect(() => {
    fetchResults();
    getGvcStatus().then(({ data }) => {
      if (data.running) {
        setJobId(data.job_id);
        setRunning(true);
      }
    }).catch(() => {});
  }, [fetchResults]);

  const toggleChannel = (ch) =>
    setChannels((prev) => prev.includes(ch) ? prev.filter((c) => c !== ch) : [...prev, ch]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const body = {};
      if (mode === "all") body.all = true;
      else if (mode === "sku") body.sku = sku.trim();
      else body.structural_only = true;
      if (channels.length) body.channels = channels;
      if (limit) body.limit = parseInt(limit, 10);

      const { data } = await runGvc(body);
      setJobId(data.job_id);
      setRunning(true);
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) {
        setError("Já existe uma auditoria em execução. Aguarde terminar.");
      } else {
        setError(err.response?.data?.detail || err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onDone = useCallback((status) => {
    setRunning(false);
    if (status === "done") fetchResults();
  }, [fetchResults]);

  const summaryERRO  = results?.filter((r) => r.status === "ERRO").length   ?? 0;
  const summaryALERT = results?.filter((r) => r.status === "ALERTA").length ?? 0;
  const summaryOK    = results?.filter((r) => r.status === "OK").length     ?? 0;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800">GVC — Auditor de Canais</h2>

      {/* Formulário */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="font-semibold text-gray-700 mb-4">Iniciar auditoria</h3>

        {running ? (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 mb-4">
            ⏳ Auditoria em andamento em background. Você pode fechar esta tela — os resultados serão salvos automaticamente ao concluir.
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Escopo</label>
              <div className="flex flex-wrap gap-3">
                {[
                  { val: "all",        label: "Todos os SKUs" },
                  { val: "sku",        label: "SKU específico" },
                  { val: "structural", label: "Apenas estrutural" },
                ].map(({ val, label }) => (
                  <label key={val} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="mode" value={val} checked={mode === val} onChange={() => setMode(val)} />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {mode === "sku" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
                <input
                  type="text" value={sku} onChange={(e) => setSku(e.target.value)}
                  placeholder="EG-FK-XXXX" required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Canais (vazio = todos)</label>
              <div className="flex gap-3 flex-wrap">
                {CHANNELS.map((ch) => (
                  <label key={ch} className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input type="checkbox" checked={channels.includes(ch)} onChange={() => toggleChannel(ch)} className="rounded" />
                    {CHANNEL_LABEL[ch] ?? ch.toUpperCase()}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Limite de SKUs (opcional)</label>
              <input
                type="number" value={limit} onChange={(e) => setLimit(e.target.value)}
                placeholder="ex: 50" min="1"
                className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {error && <p className="text-red-500 text-sm">{error}</p>}

            <button
              type="submit" disabled={submitting}
              className="bg-blue-800 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold px-6 py-2 rounded-lg text-sm transition-colors"
            >
              {submitting ? "Iniciando..." : "Iniciar Auditoria"}
            </button>
          </form>
        )}

        <JobLog jobId={jobId} onDone={onDone} />
      </div>

      {/* Resultados do banco */}
      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-700">
            Resultados da última execução
            {results !== null && <span className="ml-2 text-gray-400 font-normal text-sm">({results.length} registros)</span>}
          </h3>
          <button type="button" onClick={fetchResults} disabled={loadingResults}
            className="text-sm text-blue-700 hover:underline disabled:text-gray-400">
            {loadingResults ? "Atualizando..." : "↻ Atualizar"}
          </button>
        </div>

        {results !== null && results.length > 0 && (
          <div className="flex gap-3 mb-4">
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">{summaryERRO} ERRO</span>
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">{summaryALERT} ALERTA</span>
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">{summaryOK} OK</span>
          </div>
        )}

        {results === null ? (
          <p className="text-gray-400 text-sm">Carregando...</p>
        ) : results.length === 0 ? (
          <p className="text-gray-400 text-sm">Nenhum resultado gravado. Execute uma auditoria primeiro.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="pb-2 pr-4">SKU</th>
                  <th className="pb-2 pr-4">Canal</th>
                  <th className="pb-2 pr-4">Divergência</th>
                  <th className="pb-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {results.map((r, i) => (
                  <tr key={i}>
                    <td className="py-2 pr-4 font-mono text-xs text-gray-700">{r.sku}</td>
                    <td className="py-2 pr-4 text-gray-600">{r.canal}</td>
                    <td className="py-2 pr-4 text-gray-600 max-w-sm truncate">{r.divergencia}</td>
                    <td className="py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[r.status] || "bg-gray-100 text-gray-700"}`}>
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
